import React, { useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import BlogCard from '../components/blog/BlogCard';
import { getPostBySlug, allPosts } from '../data/blog/posts';
import { getBlogImageMeta } from '../data/blog/imageMeta';
import appStoreBadge from '../assets/images/app-store-badge.svg';
import googlePlayBadge from '../assets/images/google-play-badge.svg';
import { useStoreLinks } from '../lib/useStoreLinks';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function extractHeadings(content: string): { text: string; id: string }[] {
  const headingRegex = /^## (.+)$/gm;
  const headings: { text: string; id: string }[] = [];
  let match;
  while ((match = headingRegex.exec(content)) !== null) {
    const text = match[1]
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/`(.+?)`/g, '$1')
      .trim();
    headings.push({ text, id: slugify(text) });
  }
  return headings;
}

function getPlainText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node) return '';
  if (Array.isArray(node)) return node.map(getPlainText).join('');
  if (React.isValidElement(node) && node.props) {
    return getPlainText((node.props as any).children);
  }
  return '';
}

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  const { appStoreUrl, playStoreUrl, onAppStoreClick, onPlayStoreClick } = useStoreLinks();

  const headings = useMemo(
    () => (post ? extractHeadings(post.content) : []),
    [post],
  );

  const coverMeta = getBlogImageMeta(post?.coverImage);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formattedDate = new Date(post.publishDate + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const ogImage = post.ogImage || '/og-image.jpg';
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `https://dinksocial.com${ogImage}`;
  const articleImageUrl = post.coverImage
    ? (post.coverImage.startsWith('http') ? post.coverImage : `https://dinksocial.com${post.coverImage}`)
    : undefined;

  // Related posts: overlapping categories first, then recent, exclude current
  const related = allPosts
    .filter((p) => p.slug !== post.slug)
    .sort((a, b) => {
      const aOverlap = a.categories.some((c) => post.categories.includes(c));
      const bOverlap = b.categories.some((c) => post.categories.includes(c));
      if (aOverlap && !bOverlap) return -1;
      if (bOverlap && !aOverlap) return 1;
      return 0;
    })
    .slice(0, 2);

  // Article JSON-LD
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.publishDate,
    dateModified: post.lastModified,
    author: {
      '@type': 'Organization',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      '@id': 'https://dinksocial.com/#organization',
      name: 'DINK Social',
      logo: {
        '@type': 'ImageObject',
        url: 'https://dinksocial.com/DINKSocial_logo_plum.svg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': post.canonicalUrl,
    },
    ...(articleImageUrl ? { image: articleImageUrl } : {}),
    keywords: [post.primaryKeyword, ...post.supportingKeywords].join(', '),
  };

  // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dinksocial.com' },
      { '@type': 'ListItem', position: 2, name: 'Dink Journal', item: 'https://dinksocial.com/blog' },
      { '@type': 'ListItem', position: 3, name: post.title },
    ],
  };

  // FAQ JSON-LD (optional)
  const faqSchema =
    post.faqSchema && post.faqSchema.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: post.faqSchema.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }
      : null;

  // Custom components for ReactMarkdown
  const markdownComponents = {
    h2: ({ children, ...props }: any) => {
      const text = getPlainText(children);
      const id = slugify(text);
      return (
        <h2 id={id} {...props}>
          {children}
        </h2>
      );
    },
    img: ({ src, alt, ...props }: any) => {
      // Optional width hint via URL fragment, e.g. /photo.jpg#w600 caps that image at 600px.
      // A wrapper (not CSS min()) keeps the cap intact through react-snap's older Chromium.
      const widthHint = /#w(\d+)$/.exec(src || '');
      const meta = getBlogImageMeta(src);
      const displayWidth = widthHint ? Number(widthHint[1]) : 720;
      const image = (
        <img
          src={src}
          srcSet={meta?.srcSet}
          sizes={meta?.srcSet ? `(max-width: ${displayWidth + 48}px) 100vw, ${displayWidth}px` : undefined}
          width={meta?.width}
          height={meta?.height}
          alt={alt}
          loading="lazy"
          decoding="async"
          {...props}
          style={{
            display: 'block',
            margin: widthHint ? '0 auto' : '2.5rem auto 0.5rem',
            maxWidth: '100%',
            maxHeight: '520px',
            width: 'auto',
            height: 'auto',
            borderRadius: '24px',
          }}
        />
      );
      if (!widthHint) return image;
      return (
        <span
          style={{
            display: 'block',
            maxWidth: `${widthHint[1]}px`,
            margin: '2.5rem auto 0.5rem',
          }}
        >
          {image}
        </span>
      );
    },
    p: ({ children, node, ...props }: any) => {
      const kids = React.Children.toArray(children);
      const isCaption =
        kids.length === 1 &&
        React.isValidElement(kids[0]) &&
        (kids[0] as any).type === 'em';
      if (isCaption) {
        return (
          <p
            className="not-prose block text-center text-[13px] text-ink/60 italic mt-0 mb-10 px-4"
            {...props}
          >
            {(kids[0] as any).props.children}
          </p>
        );
      }
      return <p {...props}>{children}</p>;
    },
    blockquote: ({ children, ...props }: any) => (
      <blockquote
        className="border-l-2 border-primary pl-6 my-10 not-prose"
        {...props}
      >
        <div className="font-playfair italic text-ink text-[20px] md:text-[22px] [&_a]:text-primary [&_a]:no-underline hover:[&_a]:text-primary-dark hover:[&_a]:underline [&_strong]:text-ink [&_strong]:font-medium" style={{ lineHeight: 1.4 }}>
          {children}
        </div>
      </blockquote>
    ),
    a: ({ href, children, ...props }: any) => {
      const isExternal = href?.startsWith('http');
      return (
        <a
          href={href}
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          {...props}
        >
          {children}
        </a>
      );
    },
  };

  return (
    <div className="font-display text-ink bg-white min-h-screen">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={post.canonicalUrl} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={post.canonicalUrl} />
        <meta property="og:site_name" content="DINK Social" />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="article:published_time" content={post.publishDate} />
        <meta property="article:modified_time" content={post.lastModified} />
        <meta property="article:section" content={post.categories[0]} />
        {post.tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        {post.imageAlt && <meta name="twitter:image:alt" content={post.imageAlt} />}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <Nav />

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 md:px-8 pt-32 md:pt-40 pb-20 md:pb-28">
        {/* Back link */}
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60 hover:text-primary transition-colors mb-10"
        >
          <ArrowLeftIcon className="w-3.5 h-3.5" />
          Back to journal
        </a>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-5">
          {post.categories.map((cat) => (
            <span
              key={cat}
              className="inline-block text-[10px] uppercase tracking-[0.18em] font-medium text-primary border border-primary/25 rounded-full px-3 py-1"
            >
              {cat}
            </span>
          ))}
        </div>

        {/* Title (H1) */}
        <h1
          className="font-display font-medium text-ink text-[36px] md:text-[48px] lg:text-[56px] mb-6"
          style={{ lineHeight: 1.05, letterSpacing: '-0.02em' }}
        >
          {post.title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60 mb-10">
          <span>{formattedDate}</span>
          <span>·</span>
          <span>{`${post.readingTime} min read`}</span>
          <span>·</span>
          <span>{post.author}</span>
        </div>

        {/* Cover image */}
        {post.coverImage && (
          <div className="aspect-video overflow-hidden rounded-3xl bg-ink/5 mb-12">
            <img
              src={post.coverImage}
              srcSet={coverMeta?.srcSet}
              sizes={coverMeta?.srcSet ? '(max-width: 815px) 100vw, 720px' : undefined}
              width={coverMeta?.width}
              height={coverMeta?.height}
              alt={post.imageAlt || post.title}
              className="w-full h-full object-cover"
              decoding="async"
              {...({ fetchpriority: 'high' } as any)}
              style={{ objectPosition: post.coverImagePosition || 'center 20%' }}
            />
          </div>
        )}

        {/* Table of Contents */}
        {headings.length >= 2 && (
          <nav className="border border-ink/10 rounded-3xl px-6 py-6 md:px-8 md:py-7 mb-12">
            <p className="text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60 mb-4">
              In this article
            </p>
            <ul className="space-y-2">
              {headings.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className="text-[15px] md:text-[16px] text-ink/80 hover:text-primary transition-colors"
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Markdown content */}
        <div className="prose prose-lg max-w-none
          prose-headings:font-display prose-headings:font-medium prose-headings:text-ink prose-headings:tracking-tight
          prose-h2:text-[28px] md:prose-h2:text-[36px] prose-h2:mt-12 prose-h2:mb-5 prose-h2:leading-tight
          prose-h3:text-[22px] md:prose-h3:text-[26px] prose-h3:mt-10 prose-h3:mb-4 prose-h3:leading-tight
          prose-p:text-ink/80 prose-p:text-[17px] md:prose-p:text-[18px] prose-p:leading-relaxed
          prose-a:text-primary prose-a:no-underline prose-a:font-medium hover:prose-a:text-primary-dark hover:prose-a:underline
          prose-strong:text-ink prose-strong:font-semibold
          prose-ul:text-ink/80 prose-ol:text-ink/80
          prose-li:text-[17px] md:prose-li:text-[18px] prose-li:leading-relaxed
          prose-li:marker:text-primary/60">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {post.content}
          </ReactMarkdown>
        </div>
      </article>

      {/* CTA Banner */}
      <section className="px-6 md:px-10 pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto rounded-3xl bg-primary px-8 py-12 md:px-12 md:py-14 text-center">
          <h2
            className="font-display font-medium text-white text-[28px] md:text-[36px] mb-3"
            style={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
          >
            Ready to find your people
          </h2>
          <p className="text-white/80 text-[17px] md:text-[18px] mb-8 max-w-md mx-auto" style={{ lineHeight: 1.5 }}>
            Connect with childfree couples near you on DINK Social
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onAppStoreClick}
              className="hover:opacity-80 transition-opacity"
              aria-label="Download on the App Store"
            >
              <img src={appStoreBadge} alt="Download on the App Store" className="h-12 w-auto" />
            </a>
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onPlayStoreClick}
              className="hover:opacity-80 transition-opacity"
              aria-label="Get it on Google Play"
            >
              <img src={googlePlayBadge} alt="Get it on Google Play" className="h-12 w-auto" />
            </a>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="bg-white py-16 md:py-20 px-6 md:px-10 border-t border-ink/10">
          <div className="max-w-5xl mx-auto">
            <h2
              className="font-display font-medium text-ink text-[28px] md:text-[36px] mb-10 md:mb-12"
              style={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
            >
              More from the journal
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
              {related.map((p) => (
                <BlogCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
